/**
 * Deposit Calculator Page - Cash-in amount calculator
 *
 * Matches the design from the cash-out calculator but with deposit-specific wording.
 * Shows deposit amount in USDC, "You pay" in local currency, fees, and total.
 * On continue, navigates to location selection.
 */

import { t } from "@lingui/core/macro";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { TokenUSDC } from "@web3icons/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { CircleFlag } from "react-circle-flags";
import { RampsButton } from "@/components/ramps/Button";
import { EditableTextWithThickCaret as EditableText } from "@/components/ramps/EditableTextWithThickCaret";
import {
	SelectBottomSheet,
	type SelectOption,
} from "@/components/ramps/SelectBottomSheet";
import { useApiClientWithAutoVersion } from "@/hooks/useApiClientWithAutoVersion";
import { useCalculatorExperiment } from "@/hooks/useCalculatorExperiment";
import { useCountries } from "@/hooks/useCountries";
import { useAnalytics } from "@/hooks/usePostHog";
import { toast } from "@/lib/toast";
import { formatNumber } from "@/lib/utils";
import type { Country } from "../api";
import { DepositCalculator } from "../components/ramps/DepositCalculator";
import { RampsText } from "../components/ramps/Text";
import { Progress } from "../components/ui/progress";
import { Skeleton } from "../components/ui/skeleton";
import {
	getCountriesWithSubdivisions,
	UNSUPPORTED_US_STATES,
} from "../constants/subdivisions";
import {
	formatAmount,
	isAmountReady,
	minMaxError,
	validateAmount,
} from "../domain/calculator";
import {
	getApiVersionFromSearchParams,
	getAuthTokenFromSearchParams,
	validateRampsSearch,
} from "../lib/ramps-search-schema";
import { rampsActions, useRampsStore } from "../stores/use-ramps-store";
import { ANALYTICS_EVENTS } from "../utils/analytics";

// Types for UI components
interface CountryOption {
	value: string;
	label: string;
	countryCode: string;
}

interface RegionOption {
	value: string;
	label: string;
}

function DepositPage() {
	const search = Route.useSearch();
	const navigate = useNavigate();
	const store = useRampsStore();
	const { trackPageView, trackEvent } = useAnalytics();

	// A/B Experiment: Calculator UI test (reuse same experiment as cash-out)
	const { variant: experimentVariant, getExperimentProperties } =
		useCalculatorExperiment();

	// Get min/max amounts from store (set by PostHog feature flags)
	const minAmount = useRampsStore((state) => state.minAmount || 1);
	const maxAmount = useRampsStore((state) => state.maxAmount || 10000);

	// Centralized amount management
	const [readyForQuote, setReadyForQuote] = useState(false);
	const [inputAmount, setInputAmount] = useState("");
	const [isAmountEditable, setIsAmountEditable] = useState(true);
	const [isPendingNewQuote, setIsPendingNewQuote] = useState(false);
	const readyForQuoteTimeoutRef = useRef<NodeJS.Timeout | null>(null);

	// Bottom sheet state for country/subdivision selection
	const [showLocationSheet, setShowLocationSheet] = useState(false);
	const [locationSheetMode, setLocationSheetMode] = useState<
		"country" | "subdivision"
	>("country");

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (readyForQuoteTimeoutRef.current) {
				clearTimeout(readyForQuoteTimeoutRef.current);
			}
		};
	}, []);

	// Detect API version from URL parameters
	const authToken = getAuthTokenFromSearchParams(search) || store.token;
	const detectedVersion = getApiVersionFromSearchParams(search);

	// Set API version synchronously
	if (authToken && store.apiVersion !== detectedVersion) {
		rampsActions.setApiVersion(detectedVersion);
	}

	// Initialize API client
	const { apiClient } = useApiClientWithAutoVersion({
		authToken,
		walletAddress: search.walletAddress,
	});

	// Derive UI state from experiment variant
	const isNewCalculatorEnabled = experimentVariant === "test";

	// Track experiment-specific event once when variant is loaded
	const hasTrackedExperimentEvent = useRef(false);

	// Sync URL search params with store on route entry
	useEffect(() => {
		if (search.transaction_id) {
			rampsActions.setTransactionId(search.transaction_id);
		}
		const tokenFromUrl = search.accesstoken || search.token;
		if (tokenFromUrl) {
			rampsActions.setToken(tokenFromUrl);
		}
		if (search.walletAddress) {
			rampsActions.setWalletAddress(search.walletAddress);
		}
		if (search.amount) {
			const formattedAmount = formatAmount(search.amount);
			setInputAmount(formattedAmount);
			setIsAmountEditable(false);
		}

		// Track page view
		trackPageView("deposit", {
			page_name: "deposit",
		});
	}, [
		search.transaction_id,
		search.token,
		search.accesstoken,
		search.walletAddress,
		search.amount,
		trackPageView,
	]);

	// Track experiment-specific event
	useEffect(() => {
		if (hasTrackedExperimentEvent.current || experimentVariant === null) {
			return;
		}
		hasTrackedExperimentEvent.current = true;
		trackEvent(ANALYTICS_EVENTS.CALCULATOR_PAGE_VIEWED, {
			"$feature/ramps-new-calculator-page-enabled": experimentVariant,
			experiment_variant: experimentVariant,
			experiment_name: "calculator-ui-ab-test",
			flow_type: "deposit",
		} as Parameters<typeof trackEvent>[1]);
	}, [experimentVariant, trackEvent]);

	// Fetch service options for deposit
	const serviceOptionsQuery = useQuery({
		queryKey: [
			"serviceOptions",
			"deposit",
			store.assetCode,
			store.payoutCountry,
		],
		queryFn: async () => {
			const response = await apiClient.serviceOptions.getServiceOptions({
				operation: "deposit",
				assetCode: store.assetCode,
				userSelectedCountry: store.payoutCountry,
			});

			// Set country if API provides one and we don't have a selection
			if (response.withdrawCountry && !store.payoutCountry) {
				rampsActions.setPayoutCountry(response.withdrawCountry);
			}

			return response;
		},
		enabled: !!authToken,
		staleTime: 2 * 60 * 1000,
	});

	// Show toast notification when service options query fails
	useEffect(() => {
		if (serviceOptionsQuery.isError) {
			toast.apiError(t`Error loading fees. Please try again.`);
		}
	}, [serviceOptionsQuery.isError]);

	// Use countries hook for centralized data access
	const { filteredCountries, isLoading: countriesLoading } = useCountries({
		allowedCountryCodes: serviceOptionsQuery.data?.cashOutCountries || [],
	});

	// Country options
	const allCountries = filteredCountries || [];
	const allowedIso3 = useMemo(() => {
		const list = serviceOptionsQuery.data?.cashOutCountries || [];
		return list.length ? list : allCountries.map((c) => c.iso3Code);
	}, [serviceOptionsQuery.data?.cashOutCountries, allCountries]);

	const filteredCountriesForOptions = allCountries.filter((c) =>
		allowedIso3.includes(c.iso3Code),
	);

	// One-time country resolution
	useEffect(() => {
		const hasServiceOptions =
			!!serviceOptionsQuery.data && !serviceOptionsQuery.isLoading;
		const hasCountries = allCountries.length > 0 && !countriesLoading;
		const isReady = hasServiceOptions && hasCountries;

		if (!isReady || store.payoutCountry) {
			return;
		}

		// Default to USA if available
		const defaultCountry = allowedIso3.includes("USA") ? "USA" : allowedIso3[0];
		if (defaultCountry) {
			rampsActions.setPayoutCountry(defaultCountry);
		}
	}, [
		serviceOptionsQuery.data,
		serviceOptionsQuery.isLoading,
		allCountries,
		allowedIso3,
		countriesLoading,
		store.payoutCountry,
	]);

	// Get effective country for display
	const selectedCountryIso3 = store.payoutCountry || "USA";

	// Country options for the <Select/>
	const countryOptions: CountryOption[] = filteredCountriesForOptions.map(
		(country: Country) => ({
			value: country.iso3Code,
			label: country.name,
			countryCode: country.iso2Code.toLowerCase(),
		}),
	);

	const selectedCountry = countryOptions.find(
		(c) => c.value === selectedCountryIso3,
	);

	// Determine if selected country needs state/region selection
	const countryNeedsRegions = ["USA", "MEX", "CAN"].includes(
		selectedCountryIso3,
	);

	// Get regions for selected country
	const regionsQuery = useQuery({
		queryKey: ["regions", selectedCountryIso3],
		queryFn: async () => {
			return apiClient.referenceData.getRegions(selectedCountryIso3);
		},
		enabled: !!authToken && !!countryNeedsRegions && !!selectedCountryIso3,
		staleTime: 5 * 60 * 1000,
	});

	// Region options
	const regionOptions: RegionOption[] = (regionsQuery.data || [])
		.filter((region) => {
			if (selectedCountryIso3 === "USA") {
				return !UNSUPPORTED_US_STATES.includes(
					region.code as (typeof UNSUPPORTED_US_STATES)[number],
				);
			}
			return true;
		})
		.map((region) => ({ value: region.code, label: region.name }));

	const selectedRegion = regionOptions.find((r) => r.value === store.state);

	// Receivable currency helper
	const getPayCurrency = (): string => {
		const payoutCurrencies =
			serviceOptionsQuery.data?.serviceOptions[0]?.payoutCurrencyCodes;
		return payoutCurrencies?.[0] || "USD";
	};

	// Helper to extract display data from quote
	const getQuoteDisplayData = (quoteResponse: DepositMockQuote | undefined) => {
		if (!quoteResponse?.transactions[0]) return null;
		const transaction = quoteResponse.transactions[0];
		const sendAmountDetails = transaction.sendAmountDetails;
		const payoutAmountDetails = transaction.payoutAmountDetails;

		return {
			exchangeRate: Number(payoutAmountDetails?.fxRate || 1),
			fees: {
				value: Number(sendAmountDetails?.partnerFees?.value || 0),
				currencyCode: String(
					sendAmountDetails?.partnerFees?.currencyCode || "USD",
				),
			},
			totalPayAmount: {
				value:
					Number(inputAmount || 0) +
					Number(sendAmountDetails?.partnerFees?.value || 0),
				currencyCode: getPayCurrency(),
			},
		};
	};

	// Mock quote data for deposit flow
	// TODO: Replace with actual quote endpoint call when available
	interface DepositMockQuote {
		transactions: Array<{
			serviceOptionCode: string;
			sendAmountDetails: {
				fxRate: string;
				amount: { value: number; currencyCode: string };
				fees: { value: number; currencyCode: string };
				taxes: { value: number; currencyCode: string };
				totalAmount: { value: number; currencyCode: string };
				partnerFees: { value: number; currencyCode: string };
				promotionDetails: never[];
			};
			payoutAmountDetails: {
				fxRate: number;
				fxRateEstimated: boolean;
				amount: { value: number; currencyCode: string };
				fees: { value: number; currencyCode: string };
				taxes: { value: number; currencyCode: string };
				totalAmount: { value: number; currencyCode: string };
			};
		}>;
	}

	const quoteQuery = useQuery({
		queryKey: [
			"depositQuote",
			"deposit",
			store.payoutCountry,
			store.state,
			store.assetCode,
			inputAmount,
		],
		queryFn: async (): Promise<DepositMockQuote> => {
			// For deposit flow, return mock data with 1:1 exchange rate
			// TODO: Implement actual quote endpoint call when available
			return {
				transactions: [
					{
						serviceOptionCode: "DIRECT_TO_ACCT",
						sendAmountDetails: {
							fxRate: "1",
							amount: { value: Number(inputAmount), currencyCode: "USDC" },
							fees: { value: 0, currencyCode: "USD" },
							taxes: { value: 0, currencyCode: "USD" },
							totalAmount: { value: Number(inputAmount), currencyCode: "USDC" },
							partnerFees: { value: 2, currencyCode: "USD" }, // Example fee
							promotionDetails: [],
						},
						payoutAmountDetails: {
							fxRate: 1,
							fxRateEstimated: false,
							amount: { value: Number(inputAmount), currencyCode: "USD" },
							fees: { value: 0, currencyCode: "USD" },
							taxes: { value: 0, currencyCode: "USD" },
							totalAmount: { value: Number(inputAmount), currencyCode: "USD" },
						},
					},
				],
			};
		},
		enabled: (() => {
			const effectiveCountry = store.payoutCountry;
			const hasPayCurrency =
				!!serviceOptionsQuery.data?.serviceOptions[0]?.payoutCurrencyCodes
					?.length;

			const isValidAmountForQuote = () => {
				if (!inputAmount) return false;
				const numAmount = Number.parseFloat(inputAmount);
				if (numAmount < minAmount || numAmount > maxAmount) return false;
				if (isAmountEditable) {
					return isAmountReady(numAmount);
				}
				return !Number.isNaN(numAmount);
			};

			return (
				!!authToken &&
				!!effectiveCountry &&
				hasPayCurrency &&
				isValidAmountForQuote() &&
				readyForQuote
			);
		})(),
		staleTime: 30000,
		retry: 1,
	});

	// Auto-trigger quote for locked amounts
	useEffect(() => {
		if (
			!isAmountEditable &&
			!!authToken &&
			!!store.payoutCountry &&
			Number(search.amount) >= 1 &&
			(!countryNeedsRegions || !!store.state)
		) {
			setReadyForQuote(true);
		}
	}, [
		isAmountEditable,
		authToken,
		search.amount,
		store.payoutCountry,
		store.state,
		countryNeedsRegions,
	]);

	const handleAmountChange = async (value: string) => {
		if (readyForQuote) {
			setReadyForQuote(false);
		}
		setInputAmount(value);
		setIsPendingNewQuote(true);

		if (readyForQuoteTimeoutRef.current) {
			clearTimeout(readyForQuoteTimeoutRef.current);
		}

		const numValue = Number(value);
		if (!value || numValue < 1) return;

		readyForQuoteTimeoutRef.current = setTimeout(() => {
			const formattedAmount = formatAmount(value);
			setInputAmount(formattedAmount);
			const error = minMaxError(formattedAmount, minAmount, maxAmount);
			if (error.isError) {
				toast.validationError(error.errorMsg || "Invalid amount");
				setIsPendingNewQuote(false);
				return;
			}

			trackEvent(ANALYTICS_EVENTS.AMOUNT_CHANGED, {
				amount: Number(formattedAmount),
				source_currency: store.assetCode || "USDC",
				destination_currency: getPayCurrency(),
				flow_type: "deposit",
			} as Record<string, unknown>);

			setReadyForQuote(true);
			setIsPendingNewQuote(false);
			quoteQuery.refetch();
		}, 1500);
	};

	const handleAmountCommit = () => {
		if (readyForQuote) {
			setReadyForQuote(false);
		}
		setIsPendingNewQuote(true);

		if (readyForQuoteTimeoutRef.current) {
			clearTimeout(readyForQuoteTimeoutRef.current);
		}

		if (!inputAmount || Number(inputAmount) < 1) return;

		setInputAmount(formatAmount(inputAmount));
		const error = minMaxError(inputAmount, minAmount, maxAmount);
		if (error.isError) {
			toast.validationError(error.errorMsg || "Invalid amount");
			setIsPendingNewQuote(false);
			return;
		}

		setReadyForQuote(true);
		setIsPendingNewQuote(false);
		quoteQuery.refetch();
	};

	// Event handlers for country/state
	const handleCountryChange = (newIso3: string) => {
		setReadyForQuote(false);
		rampsActions.setPayoutCountry(newIso3);
		rampsActions.setCalculatorData({
			country: newIso3,
			state: "",
			amount: inputAmount || "0.00",
			currency: store.assetCode || "USDC",
		});

		setTimeout(() => {
			if (inputAmount && Number(inputAmount) >= 1) {
				setReadyForQuote(true);
			}
		}, 100);
	};

	const handleStateChange = (newState: string) => {
		rampsActions.setCalculatorData({
			country: selectedCountryIso3,
			state: newState,
			amount: inputAmount || "0.00",
			currency: store.assetCode || "USDC",
		});
	};

	// Handler for bottom sheet location selection
	const handleLocationSelect = (value: string, _option: SelectOption) => {
		if (locationSheetMode === "country") {
			handleCountryChange(value);

			const countriesWithSubdivisions = getCountriesWithSubdivisions();
			if (countriesWithSubdivisions.includes(value)) {
				setLocationSheetMode("subdivision");
			} else {
				setShowLocationSheet(false);
				setLocationSheetMode("country");
			}
		} else {
			handleStateChange(value);
			setShowLocationSheet(false);
			setLocationSheetMode("country");
		}
	};

	// Open location sheet
	const openLocationSheet = () => {
		const countriesWithSubdivisions = getCountriesWithSubdivisions();
		if (countriesWithSubdivisions.includes(selectedCountryIso3)) {
			setLocationSheetMode("subdivision");
		} else {
			setLocationSheetMode("country");
		}
		setShowLocationSheet(true);
	};

	const handleContinue = () => {
		// Validate amount
		const amountValidation = validateAmount(inputAmount);
		if (!amountValidation.isValid) {
			const errorMsg = amountValidation.error || t`Invalid amount`;
			toast.validationError(errorMsg);
			return;
		}

		// Store the amount and set service option for deposit flow
		rampsActions.setCalculatorData({
			country: store.payoutCountry,
			state: store.state,
			amount: inputAmount || "0.00",
			currency: store.assetCode || "USDC",
		});
		rampsActions.setServiceOption("DIRECT_TO_ACCT");

		// Track continue click
		if (experimentVariant !== null) {
			trackEvent(ANALYTICS_EVENTS.CALCULATOR_CONTINUE_CLICKED, {
				...getExperimentProperties(),
				flow_type: "deposit",
				from_review: search.from_review === "true",
			} as Record<string, unknown>);
		}

		// If coming from review (editing amount), return to review directly
		if (search.from_review === "true") {
			const searchWithoutFromReview = { ...search };
			delete searchWithoutFromReview.from_review;
			navigate({
				to: "/ramps/review",
				search: { ...searchWithoutFromReview, step: "review" },
			});
			return;
		}

		// Navigate to location selection with search params
		navigate({ to: "/ramps/deposit-location", search });
	};

	// Convert options to SelectOption format
	const countrySelectOptions: SelectOption[] = countryOptions.map((c) => ({
		value: c.value,
		label: c.label,
		countryCode: c.countryCode,
	}));

	const subdivisionSelectOptions: SelectOption[] = regionOptions.map((r) => ({
		value: r.value,
		label: r.label,
	}));

	// Get display text for current location selection
	const getLocationDisplayText = (): string => {
		if (countriesLoading) {
			return t`Loading...`;
		}
		if (countryNeedsRegions && store.state && selectedRegion) {
			return selectedRegion.label;
		}
		if (countryNeedsRegions && !store.state) {
			return selectedCountryIso3 === "USA"
				? t`Select a state`
				: t`Select a region`;
		}
		return selectedCountry?.label || t`Select location`;
	};

	// Get quote display data
	const quoteDisplayData = getQuoteDisplayData(quoteQuery.data);
	const depositQuoteData = quoteDisplayData
		? {
				exchangeRate: quoteDisplayData.exchangeRate,
				fees: quoteDisplayData.fees,
				totalPayAmount: quoteDisplayData.totalPayAmount,
			}
		: null;

	return (
		<div className="flex flex-col min-h-screen max-w-screen-sm mx-auto">
			<div className="px-4 bg-ramps-background-base">
				<div className="flex items-center justify-center py-6">
					{/* Country/Location selector trigger */}
					<div className="mx-4">
						<button
							type="button"
							onClick={openLocationSheet}
							disabled={countriesLoading}
							className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg border-none hover:opacity-80 transition-opacity bg-ramps-background-surface-2 text-ramps-text-primary disabled:opacity-50 disabled:cursor-not-allowed"
						>
							<div className="flex items-center gap-2">
								{countriesLoading ? (
									<div className="w-5 h-5 rounded-full bg-gray-300 animate-pulse" />
								) : (
									<CircleFlag
										countryCode={selectedCountry?.countryCode || "us"}
										className="w-5 h-5"
									/>
								)}
								<RampsText
									variant="body-default"
									className="font-medium"
									color="primary"
								>
									{getLocationDisplayText()}
								</RampsText>
							</div>
						</button>
					</div>
				</div>

				{/* Progress bar */}
				<div className="mb-8">
					<Progress
						value={10}
						className="h-1 bg-ramps-background-surface-2 *:bg-ramps-text-primary"
						data-testid="deposit-progress"
					/>
				</div>

				{/* Location Selection Bottom Sheet */}
				<SelectBottomSheet
					key={locationSheetMode}
					open={showLocationSheet}
					onOpenChange={(open) => {
						if (!open) {
							setShowLocationSheet(false);
							setLocationSheetMode("country");
						}
					}}
					title={
						locationSheetMode === "country"
							? t`Select country`
							: selectedCountryIso3 === "USA"
								? t`Select state`
								: t`Select region`
					}
					searchPlaceholder={
						locationSheetMode === "country"
							? t`Search countries...`
							: t`Search...`
					}
					options={
						locationSheetMode === "country"
							? countrySelectOptions
							: subdivisionSelectOptions
					}
					value={
						locationSheetMode === "country" ? selectedCountryIso3 : store.state
					}
					onSelect={handleLocationSelect}
					disabledValues={
						locationSheetMode === "subdivision" && selectedCountryIso3 === "USA"
							? [...UNSUPPORTED_US_STATES]
							: []
					}
					renderOptionIcon={(option) =>
						locationSheetMode === "country" && option.countryCode ? (
							<CircleFlag
								countryCode={option.countryCode}
								className="w-6 h-6"
							/>
						) : null
					}
					showSearch={true}
					onBack={
						locationSheetMode === "subdivision"
							? () => setLocationSheetMode("country")
							: undefined
					}
				/>

				{/* Deposit calculator section - A/B test with feature flag */}
				{isNewCalculatorEnabled ? (
					<div className="mb-16">
						<DepositCalculator
							inputAmount={inputAmount}
							isAmountEditable={isAmountEditable}
							onAmountChange={handleAmountChange}
							onAmountCommit={handleAmountCommit}
							assetCode={store.assetCode || "USDC"}
							payCurrency={getPayCurrency()}
							countryCode={selectedCountry?.countryCode || "us"}
							quoteData={depositQuoteData}
							isLoading={quoteQuery.isLoading}
						/>
					</div>
				) : (
					<div className="text-center mb-16">
						<RampsText variant="body-bold" color="secondary" className="mb-2">
							{t`Deposit amount`}
						</RampsText>

						<div className="flex items-center justify-center gap-3 mb-6">
							<EditableText
								value={inputAmount}
								isEditable={isAmountEditable}
								onChange={handleAmountChange}
								onCommit={handleAmountCommit}
								className="text-6xl font-normal"
								variant="number-md"
								color="primary"
								placeholder="0.00"
								caretWidthPx={4}
								blinkMs={900}
							/>
							<div className="flex flex-col items-center gap-0">
								<TokenUSDC className="w-6 h-6" />
								<RampsText variant="body-bold" color="disabled">
									{store.assetCode}
								</RampsText>
							</div>
						</div>

						{/* You pay section */}
						<div className="mb-6">
							<RampsText variant="body-bold" color="secondary" className="mb-2">
								{t`You pay`}
							</RampsText>

							<div className="flex items-center justify-center gap-3">
								{quoteQuery.isLoading ? (
									<Skeleton className="h-16 w-40 bg-gray-200/30 border border-gray-300/20" />
								) : (
									<RampsText
										variant="number-md"
										color="primary"
										className="text-6xl font-normal"
									>
										{inputAmount ? formatNumber(Number(inputAmount)) : "0.00"}
									</RampsText>
								)}
								<div className="flex flex-col items-center gap-0.5">
									<CircleFlag
										countryCode={selectedCountry?.countryCode || "us"}
										className="w-4 h-4"
									/>
									<RampsText variant="body-bold" color="disabled">
										{getPayCurrency()}
									</RampsText>
								</div>
							</div>
						</div>

						{/* Exchange rate one-liner */}
						{(() => {
							if (quoteDisplayData && inputAmount) {
								return (
									<RampsText variant="body-default" color="primary">
										{`${t`Exchange rate:`} 1 ${getPayCurrency()} = ${formatNumber(1 / quoteDisplayData.exchangeRate, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${store.assetCode || "USDC"}`}
									</RampsText>
								);
							}
							if (quoteQuery.isLoading) {
								return (
									<RampsText variant="body-default" color="primary">
										{t`Loading exchange rate...`}
									</RampsText>
								);
							}
							return <div className="h-6" />;
						})()}

						<div className="h-6" />
					</div>
				)}
			</div>

			{/* Fees section */}
			<div className="rounded-t-2xl p-6 mt-auto bg-gradient-ramps-status-bl">
				<div className="flex justify-between items-center mb-3">
					<RampsText variant="body-default" color="primary">
						{t`Fees`}
					</RampsText>
					{quoteQuery.isLoading ? (
						<Skeleton className="h-5 w-20 bg-gray-200/30 border border-gray-300/20" />
					) : (
						<RampsText variant="body-default" color="primary">
							{(() => {
								if (quoteDisplayData && inputAmount) {
									return `${formatNumber(quoteDisplayData.fees.value)} ${quoteDisplayData.fees.currencyCode}`;
								}
								return `${formatNumber(0)} ${getPayCurrency()}`;
							})()}
						</RampsText>
					)}
				</div>

				{/* Total row */}
				<div className="flex justify-between items-center mb-2">
					<RampsText variant="body-bold" color="primary">
						{t`Total`}
					</RampsText>
					{quoteQuery.isLoading ? (
						<Skeleton className="h-6 w-24 bg-gray-200/30 border border-gray-300/20" />
					) : (
						<RampsText variant="body-bold" color="primary">
							{(() => {
								if (quoteDisplayData && inputAmount) {
									return `${formatNumber(quoteDisplayData.totalPayAmount.value)} ${quoteDisplayData.totalPayAmount.currencyCode}`;
								}
								return `${formatNumber(0)} ${getPayCurrency()}`;
							})()}
						</RampsText>
					)}
				</div>

				{/* Fee disclaimer message */}
				<p className="text-xs text-primary mb-6">
					{t`Fee and total will be provided after you select a location`}
				</p>

				{/* Continue Button */}
				<RampsButton
					onClick={handleContinue}
					disabled={
						!store.payoutCountry ||
						(countryNeedsRegions && !store.state) ||
						!inputAmount ||
						isPendingNewQuote ||
						quoteQuery.isFetching ||
						minMaxError(inputAmount, minAmount, maxAmount).isError
					}
					loading={quoteQuery.isFetching}
					className="w-full h-12 rounded-lg font-medium bg-black text-white hover:bg-gray-800"
				>
					{t`Continue`}
				</RampsButton>
			</div>
		</div>
	);
}

export const Route = createFileRoute("/ramps/deposit")({
	validateSearch: (search) => validateRampsSearch(search),
	component: DepositPage,
});
